import React, { useState, useEffect } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "حاسبة الطاقة الحرة Gibbs - حساب تلقائية التفاعلات الكيميائية";
const DESCRIPTION =
  "حاسبة الطاقة الحرة جيبس (Gibbs Free Energy) المجانية. احسب ΔG للتفاعلات الكيميائية باستخدام المحتوى الحراري، الإنتروبيا، ودرجة الحرارة. تعرف على تلقائية التفاعلات بدقة.";
const KEYWORDS =
  "طاقة جيبس الحرة, حاسبة Gibbs, حساب تلقائية التفاعل, المحتوى الحراري, الإنتروبيا, الكيمياء الحرارية, ΔG calculator, Gibbs free energy calculation";
const OG_URL = "https://do-calculate.com/calculator/ar/gibbs-free-energy/";

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "حاسبة الطاقة الحرة جيبس",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "ScienceApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "الرئيسية",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "الحاسبات",
        item: "https://do-calculate.com/ar/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "حاسبة الطاقة الحرة جيبس",
        item: OG_URL,
      },
    ],
  },
];

function GibbsFreeEnergyCalculator() {
  const [enthalpy, setEnthalpy] = useState("");
  const [entropy, setEntropy] = useState("");
  const [temperature, setTemperature] = useState("");
  const [tempUnit, setTempUnit] = useState("K");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const calculateGibbs = () => {
    try {
      setError("");
      const H = parseFloat(enthalpy);
      const S = parseFloat(entropy);
      const T_raw = parseFloat(temperature);

      if (isNaN(H) || isNaN(S) || isNaN(T_raw)) {
        setResult(null);
        return;
      }

      // Convert Temperature to Kelvin
      let T_kelvin = T_raw;
      if (tempUnit === "C") T_kelvin = T_raw + 273.15;
      if (tempUnit === "F") T_kelvin = (T_raw - 32) * (5 / 9) + 273.15;

      if (T_kelvin < 0) {
        setError("درجة الحرارة لا يمكن أن تكون أقل من الصفر المطلق.");
        setResult(null);
        return;
      }

      // Formula: ΔG = ΔH - TΔS
      // Note: H is usually in kJ/mol, S is in J/(mol·K). 
      // We convert S to kJ by dividing by 1000.
      const S_kJ = S / 1000;
      const deltaG = H - T_kelvin * S_kJ;

      let spontaneity = "";
      let color = "";

      if (deltaG < 0) {
        spontaneity = "تفاعل تلقائي (Spontaneous)";
        color = "#1a5f3f";
      } else if (deltaG > 0) {
        spontaneity = "تفاعل غير تلقائي (Non-spontaneous)";
        color = "#b02a37";
      } else {
        spontaneity = "التفاعل في حالة اتزان (Equilibrium)";
        color = "#084298";
      }

      setResult({
        deltaG: deltaG.toFixed(4),
        spontaneity,
        color,
        T_kelvin: T_kelvin.toFixed(2),
      });
    } catch (err) {
      setError("حدث خطأ أثناء الحساب.");
      setResult(null);
    }
  };

  useEffect(() => {
    calculateGibbs();
  }, [enthalpy, entropy, temperature, tempUnit]);

  const handleClear = () => {
    setEnthalpy("");
    setEntropy("");
    setTemperature("");
    setTempUnit("K");
    setResult(null);
    setError("");
  };

  return (
    <div className="rtl" style={{ direction: "rtl" }}>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "5px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>حاسبة الطاقة الحرة جيبس (Gibbs)</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                تستخدم <strong>حاسبة الطاقة الحرة جيبس</strong> لتحديد ما إذا كان التفاعل الكيميائي سيحدث بشكل تلقائي أم لا. من خلال إدخال التغير في المحتوى الحراري (Enthalpy)، والتغير في الإنتروبيا (Entropy)، ودرجة الحرارة، يمكنك معرفة قيمة <strong>ΔG</strong> وحالة تلقائية التفاعل فوراً.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />

            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>أدخل بيانات التفاعل الكيميائي</h2>
              </div>

              <div className="row">
                <div className="col-sm-12 col-md-6">
                  <label >التغير في المحتوى الحراري ΔH (kJ/mol)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={enthalpy}
                    onChange={(e) => setEnthalpy(e.target.value)}
                    placeholder="مثال: -92.2"
                  />
                </div>
                <div className="col-sm-12 col-md-6">
                  <label >التغير في الإنتروبيا ΔS (J/mol·K)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={entropy}
                    onChange={(e) => setEntropy(e.target.value)}
                    placeholder="مثال: -198.7"
                  />
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-sm-12 col-md-6">
                  <label >درجة الحرارة</label>
                  <input
                    type="number"
                    className="form-control"
                    value={temperature}
                    onChange={(e) => setTemperature(e.target.value)}
                    placeholder="أدخل القيمة"
                  />
                </div>
                <div className="col-sm-12 col-md-6">
                  <label >الوحدة</label>
                  <select
                    className="form-select"
                    value={tempUnit}
                    onChange={(e) => setTempUnit(e.target.value)}
                  >
                    <option value="K">كلفن (K)</option>
                    <option value="C">سليزيوس (C°)</option>
                    <option value="F">فهرنهايت (F°)</option>
                  </select>
                </div>
              </div>

              <div className="text-center mb-3 mt-4">
                <button onClick={handleClear} className="btn btn-secondary" style={{ padding: "10px 30px" }}>
                  امسح البيانات
                </button>
              </div>

              {error && (
                <div className="alert alert-warning" role="alert">
                  <strong>تنبيه:</strong> {error}
                </div>
              )}

              {result && (
                <div className="mt-4">
                  <h3 style={{ fontSize: "18px", marginBottom: "15px", textAlign: "center" }}>
                    <strong>النتيجة النهائية:</strong>
                  </h3>
                  <div className="mb-3">
                    <table className="table bmi-table" style={{ width: "100%" }}>
                      <thead>
                        <tr>
                          <th className="bmi-header" style={{ textAlign: "center" }}>البيان</th>
                          <th className="bmi-header" style={{ textAlign: "center" }}>القيمة</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ textAlign: "center" }}>طاقة جيبس الحرة (ΔG)</td>
                          <td style={{ textAlign: "center", fontSize: "18px", fontWeight: "bold", color: result.color }}>
                            {result.deltaG} kJ/mol
                          </td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>حالة التلقائية</td>
                          <td style={{ textAlign: "center", fontWeight: "bold", color: result.color }}>
                            {result.spontaneity}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>الحرارة بالكلفن</td>
                          <td style={{ textAlign: "center" }}>{result.T_kelvin} K</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            <hr />
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>

      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <strong>ما هي طاقة جيبس الحرة (Gibbs Free Energy)؟</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  طاقة جيبس الحرة هي كمية ديناميكية حرارية تستخدم لقياس أقصى شغل مفيد يمكن الحصول عليه من نظام كيميائي عند ضغط ودرجة حرارة ثابتين. تعتبر هذه القيمة هي "المعيار الذهبي" لتوقع اتجاه التفاعلات الكيميائية وتوازنها.
                </p>
                <ul style={{ lineHeight: "1.8" }}>
                  <li><strong>ΔG سلبية:</strong> التفاعل تلقائي ويحرر طاقة.</li>
                  <li><strong>ΔG إيجابية:</strong> التفاعل غير تلقائي ويحتاج لامتصاص طاقة للحدوث.</li>
                  <li><strong>ΔG تساوي صفر:</strong> النظام في حالة اتزان ديناميكي.</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>كيفية حساب طاقة جيبس الحرة</strong>
              </div>
              <div className="info-section-body">
                <p>تعتمد الحاسبة على المعادلة الشهيرة:</p>
                <div className="alert alert-info text-center" style={{ fontSize: "20px" }}>
                  <strong>ΔG = ΔH - TΔS</strong>
                </div>
                <ul style={{ lineHeight: "1.8" }}>
                  <li><strong>ΔG:</strong> التغير في طاقة جيبس الحرة (kJ/mol).</li>
                  <li><strong>ΔH:</strong> التغير في المحتوى الحراري (Enthalpy)، ويقيس الحرارة الممتصة أو المنبعثة.</li>
                  <li><strong>T:</strong> درجة الحرارة المطلقة مقاسة بالكلفن (K).</li>
                  <li><strong>ΔS:</strong> التغير في الإنتروبيا (Entropy)، ويقيس العشوائية أو الفوضى في النظام.</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>تأثير الحرارة على تلقائية التفاعل</strong>
              </div>
              <div className="info-section-body">
                <table className="table bmi-table">
                  <thead>
                    <tr>
                      <th >ΔH</th>
                      <th >ΔS</th>
                      <th >النتيجة (ΔG)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>سالبة (-)</td>
                      <td>موجبة (+)</td>
                      <td>تلقائي دائماً عند جميع درجات الحرارة</td>
                    </tr>
                    <tr>
                      <td>موجبة (+)</td>
                      <td>سالبة (-)</td>
                      <td>غير تلقائي دائماً</td>
                    </tr>
                    <tr>
                      <td>سالبة (-)</td>
                      <td>سالبة (-)</td>
                      <td>تلقائي عند درجات الحرارة المنخفضة</td>
                    </tr>
                    <tr>
                      <td>موجبة (+)</td>
                      <td>موجبة (+)</td>
                      <td>تلقائي عند درجات الحرارة العالية</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>أمثلة عملية</strong>
              </div>
              <div className="info-section-body">
                <p><strong>مثال 1:</strong> تفاعل تكون الأمونيا عند 298 كلفن.</p>
                <ul>
                  <li>ΔH = -92.22 kJ/mol</li>
                  <li>ΔS = -198.75 J/mol·K</li>
                  <li>الحساب: ΔG = -92.22 - (298 * -0.19875) = -33.0 kJ/mol</li>
                  <li><strong>النتيجة:</strong> التفاعل تلقائي.</li>
                </ul>
                <p><strong>مثال 2:</strong> تفاعل يحتاج لامتصاص حرارة عالية (ΔH موجبة) مع عشوائية منخفضة (ΔS سالبة).</p>
                <ul>
                  <li>ستكون ΔG دائماً موجبة، مما يعني أن التفاعل لن يحدث أبداً بدون تدخل طاقة خارجية مستمرة.</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>نصائح للاستخدام الصحيح</strong>
              </div>
              <div className="info-section-body">
                <ul style={{ lineHeight: "1.8" }}>
                  <li><strong>انتبه للوحدات:</strong> غالباً ما تكون ΔS بالـ Joules بينما ΔH بالـ KiloJoules. الحاسبة تقوم بالتحويل تلقائياً، فقط تأكد من إدخال القيم كما هي في الجداول الكيميائية.</li>
                  <li><strong>الصفر المطلق:</strong> لا يمكن حساب التفاعلات عند درجة حرارة 0 كلفن أو أقل في الواقع الفيزيائي.</li>
                  <li><strong>الدقة:</strong> استخدم القيم القياسية (Standard values) عند 25 درجة مئوية للحصول على نتائج مطابقة للكتب المدرسية.</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>الأسئلة الشائعة حول طاقة جيبس</strong>
              </div>
              <div className="info-section-body">
                <div className="mb-3">
                  <strong>1. ماذا يعني إذا كانت ΔG تساوي صفراً؟</strong>
                  <p>هذا يعني أن النظام في حالة توازن كيميائي، حيث يتساوى معدل التفاعل الأمامي مع معدل التفاعل العكسي.</p>
                </div>
                <div className="mb-3">
                  <strong>2. هل التفاعل التلقائي يحدث بسرعة؟</strong>
                  <p>ليس بالضرورة. ΔG تخبرنا "هل" سيحدث التفاعل، لكنها لا تخبرنا عن "سرعة" حدوثه (هذا تخصص الحركية الكيميائية وليس الديناميكا الحرارية).</p>
                </div>
                <div className="mb-3">
                  <strong>3. كيف أحول السليزيوس إلى كلفن؟</strong>
                  <p>المعادلة بسيطة: K = C + 273.15. الحاسبة تقوم بذلك نيابة عنك عند اختيار وحدة C.</p>
                </div>
                <div className="mb-3">
                  <strong>4. لماذا تعتبر الإنتروبيا مهمة في الحساب؟</strong>
                  <p>لأن الكون يميل طبيعياً نحو زيادة الفوضى. التفاعلات التي تزيد من عشوائية النظام يسهل حدوثها تلقائياً.</p>
                </div>
                <div className="mb-3">
                  <strong>5. هل يمكن أن تتغير تلقائية التفاعل بتغير الحرارة؟</strong>
                  <p>نعم، التفاعلات التي لها نفس الإشارة لـ ΔH و ΔS (كلاهما موجب أو كلاهما سالب) تتأثر تلقائيتها بشكل مباشر برفع أو خفض درجة الحرارة.</p>
                </div>
                <div className="mb-3">
                  <strong>6. ما الفرق بين ΔG و ΔG°؟</strong>
                  <p>ΔG° هي طاقة جيبس في الظروف القياسية (تركيز 1 مولار، ضغط 1 ضغط جوي)، بينما ΔG هي الطاقة في أي ظروف حالية أخرى.</p>
                </div>
                <div className="mb-3">
                  <strong>7. ما هي وحدات ΔG الأكثر شيوعاً؟</strong>
                  <p>الوحدة الأكثر شيوعاً هي kJ/mol، وهي المستخدمة في نتائج هذه الحاسبة.</p>
                </div>
                <div className="mb-3">
                  <strong>8. هل يمكن استخدام الحاسبة لتفاعلات الاحتراق؟</strong>
                  <p>بالتأكيد، تفاعلات الاحتراق هي مثال كلاسيكي حيث تكون ΔH سالبة جداً مما يجعل التفاعل تلقائياً للغاية.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GibbsFreeEnergyCalculator;